import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, BookOpen, Briefcase, GraduationCap, Image, ChevronRight, Library } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ModeToggle } from "@/components/mode-toggle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const intentOptions = [
  { value: "understand", label: "Understand", icon: Sparkles },
  { value: "study", label: "Study", icon: GraduationCap },
  { value: "work", label: "Work", icon: Briefcase },
];

const levelOptions = [
  { value: "new", label: "New to this" },
  { value: "some", label: "Some knowledge" },
  { value: "advanced", label: "Advanced" },
];

export default function HomePage() {
  const navigate = useNavigate();
  const [inputMode, setInputMode] = useState("ask");
  const [inputText, setInputText] = useState("");
  const [intent, setIntent] = useState("understand");
  const [level, setLevel] = useState("new");
  const [visualsFirst, setVisualsFirst] = useState(false);
  const [useGeneralKnowledge, setUseGeneralKnowledge] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    if (!inputText.trim()) {
      toast.error("Please enter a topic or text to understand");
      return;
    }

    setIsGenerating(true);

    try {
      const response = await fetch(`${API}/topics/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: inputText,
          mode: inputMode,
          intent,
          level,
          visuals_first: visualsFirst,
          use_general_knowledge: useGeneralKnowledge,
        }),
      });

      if (!response.ok) throw new Error("Failed to generate");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let topicId = null;
      let hasError = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === "start" || data.type === "done") {
                topicId = data.topic_id;
              }
              if (data.type === "error") {
                hasError = true;
                const errorMsg = data.message || "Generation failed";
                if (errorMsg.includes("budget") || errorMsg.includes("Budget")) {
                  toast.error("API key budget exceeded. Please add balance in Profile → Universal Key.");
                } else {
                  toast.error(errorMsg);
                }
              }
            } catch (e) {
              // Skip parsing errors
            }
          }
        }
      }

      if (topicId && !hasError) {
        navigate(`/topic/${topicId}`);
      } else if (!hasError) {
        toast.error("Generation incomplete. Please try again.");
      }
    } catch (error) {
      const errorMsg = error.message || "Failed to generate understanding";
      if (errorMsg.includes("budget") || errorMsg.includes("Budget")) {
        toast.error("API key budget exceeded. Please add balance in Profile → Universal Key.");
      } else {
        toast.error("Failed to generate understanding. Please try again.");
      }
      console.error(error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="w-full py-6 px-8 flex items-center justify-between">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-3"
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold tracking-tight">ConceptKit</span>
        </motion.div>

        <div className="flex items-center gap-4">
          <ModeToggle />
          <Button
            variant="ghost"
            onClick={() => navigate("/library")}
            className="gap-2 text-muted-foreground hover:text-foreground"
            data-testid="library-nav-btn"
          >
            <Library className="w-4 h-4" />
            Library
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-4">
            Understand anything
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-indigo-500">
              from every angle
            </span>
          </h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Multiple perspectives, visual maps, and active recall — all in under 2 minutes.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="w-full max-w-2xl"
        >
          {/* Input Tabs */}
          <Tabs value={inputMode} onValueChange={setInputMode} className="w-full">
            <TabsList className="bg-muted/60 backdrop-blur-md rounded-full p-1 mb-8 w-fit mx-auto border border-border">
              <TabsTrigger
                value="ask"
                className="rounded-full px-6 py-2 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm focus-visible:ring-0 focus-visible:ring-offset-0"
                data-testid="ask-tab"
              >
                <BookOpen className="w-4 h-4 mr-2 shrink-0" />
                Ask
              </TabsTrigger>
              <TabsTrigger
                value="paste"
                className="rounded-full px-6 py-2 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm focus-visible:ring-0 focus-visible:ring-offset-0"
                data-testid="paste-tab"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Paste
              </TabsTrigger>
            </TabsList>

            <TabsContent value="ask" className="mt-0">
              <div className="relative">
                <Textarea
                  placeholder="What do you want to understand? e.g., 'How does machine learning work?'"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  className="min-h-[140px] text-lg bg-background border-border rounded-2xl p-6 resize-none focus:border-primary/50 focus:ring-primary/20 shadow-sm"
                  data-testid="topic-input"
                />
              </div>
            </TabsContent>

            <TabsContent value="paste" className="mt-0">
              <div className="relative">
                <Textarea
                  placeholder="Paste the text you want to understand (articles, documentation, notes...)"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  className="min-h-[200px] text-base bg-background border-border rounded-2xl p-6 resize-none focus:border-primary/50 focus:ring-primary/20 shadow-sm"
                  data-testid="paste-input"
                />
                <div className="absolute bottom-4 right-4 text-xs text-muted-foreground">
                  {inputText.length} characters
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Intent Chips */}
          <div className="mt-6 space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Label className="text-sm text-muted-foreground uppercase tracking-widest mr-2">Intent:</Label>
              {intentOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setIntent(option.value)}
                  className={`chip flex items-center gap-2 ${intent === option.value ? "chip-active" : ""}`}
                  data-testid={`intent-${option.value}`}
                >
                  <option.icon className="w-4 h-4" />
                  {option.label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Label className="text-sm text-muted-foreground uppercase tracking-widest mr-2">Level:</Label>
              {levelOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setLevel(option.value)}
                  className={`chip ${level === option.value ? "chip-active" : ""}`}
                  data-testid={`level-${option.value}`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-6 pt-2">
              <div className="flex items-center gap-3">
                <Switch
                  id="visuals"
                  checked={visualsFirst}
                  onCheckedChange={setVisualsFirst}
                  data-testid="visuals-first-toggle"
                />
                <Label htmlFor="visuals" className="flex items-center gap-2 cursor-pointer">
                  <Image className="w-4 h-4 text-muted-foreground" />
                  Visuals first
                </Label>
              </div>

              {inputMode === "paste" && (
                <div className="flex items-center gap-3">
                  <Switch
                    id="general"
                    checked={useGeneralKnowledge}
                    onCheckedChange={setUseGeneralKnowledge}
                    data-testid="general-knowledge-toggle"
                  />
                  <Label htmlFor="general" className="cursor-pointer text-sm">
                    Also use general knowledge
                  </Label>
                </div>
              )}
            </div>
          </div>

          {/* Generate Button */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-10 flex justify-center"
          >
            <Button
              onClick={handleGenerate}
              disabled={isGenerating || !inputText.trim()}
              className="rounded-full px-10 py-7 text-lg font-bold bg-gradient-to-r from-cyan-500 to-indigo-600 hover:from-cyan-400 hover:to-indigo-500 text-white shadow-[0_0_30px_-5px_rgba(6,182,212,0.5)] hover:shadow-[0_0_40px_-5px_rgba(6,182,212,0.7)] transition-all duration-300 disabled:opacity-50"
              data-testid="generate-btn"
            >
              {isGenerating ? (
                <>
                  <span className="animate-pulse-glow">Generating...</span>
                </>
              ) : (
                <>
                  Generate Understanding
                  <ChevronRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </motion.div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-sm text-muted-foreground">
        <p>Multi-perspective learning with visual mental models</p>
      </footer>
    </div>
  );
}
